import { Command } from "cmdk";
import {
  AppWindow,
  Compass,
  FolderKanban,
  FolderPlus,
  Keyboard,
  MessagesSquare,
  Palette,
  Puzzle,
  Settings,
  UserRound,
  Users,
} from "lucide-react";
import { Dialog as D } from "radix-ui";
import { useNavigate } from "react-router";
import type { DesktopCommand } from "@ide/shared";
import { useMe, useProjects } from "@/lib/queries";
import { desktop } from "./bridge";
import { isAvailable, runCommand } from "./commands";
import { FULL_EDITION } from "./edition";
import { useDesktopUi } from "./ui";

const PAGES: Array<{ label: string; command: DesktopCommand; icon: typeof Compass; full?: boolean }> = [
  { label: "Projetos", command: "go-projects", icon: FolderKanban },
  { label: "Mensagens", command: "go-chat", icon: MessagesSquare },
  { label: "Descobrir devs", command: "go-discover", icon: Compass, full: true },
  { label: "Grupos", command: "go-groups", icon: Users, full: true },
  { label: "Plugins", command: "go-plugins", icon: Puzzle, full: true },
  { label: "Meu perfil", command: "go-profile", icon: UserRound, full: true },
];

/**
 * Busca rápida (Ctrl/⌘+P fora de um projeto; Ctrl/⌘+K na web): abrir projetos, ir para páginas e
 * rodar comandos. Dentro de um projeto, o mesmo atalho abre a paleta do editor.
 */
export function QuickOpen() {
  const mode = useDesktopUi((s) => s.quickOpen);
  const set = useDesktopUi((s) => s.set);
  const { data: me } = useMe();
  const { data: projects } = useProjects(Boolean(me) && mode !== null);
  const navigate = useNavigate();
  const close = () => set({ quickOpen: null });
  const act = (fn: () => void) => () => {
    close();
    fn();
  };
  const item =
    "flex h-9 cursor-default items-center gap-2.5 rounded-lg px-2.5 text-[13px] data-[selected=true]:bg-fg data-[selected=true]:text-fg-inverse";
  return (
    <D.Root open={mode !== null} onOpenChange={(open) => !open && close()}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-[80] bg-overlay" />
        <D.Content className="glass fixed left-1/2 top-[12vh] z-[80] w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2 animate-pop-in overflow-hidden rounded-2xl border border-border-strong shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6),inset_0_1px_0_0_var(--highlight)]">
          <D.Title className="sr-only">Busca rápida</D.Title>
          <D.Description className="sr-only">Abra projetos, páginas e comandos</D.Description>
          <Command label="Busca rápida" loop>
            <Command.Input
              placeholder={mode === "projects" ? "Abrir projeto…" : "Buscar projeto, página ou comando…"}
              className="h-12 w-full border-b border-border bg-transparent px-4 text-[14px] outline-none placeholder:text-fg-subtle"
              autoFocus
            />
            <Command.List className="max-h-[22rem] overflow-y-auto p-1.5">
              <Command.Empty className="px-3 py-6 text-center text-[13px] text-fg-subtle">
                Nada encontrado.
              </Command.Empty>
              <Command.Group heading="Projetos">
                {(projects ?? []).map((p) => (
                  <Command.Item
                    key={p.id}
                    value={`projeto ${p.name} ${p.description ?? ""}`}
                    onSelect={act(() => navigate(`/p/${p.id}`))}
                    className={item}
                  >
                    <FolderKanban className="h-3.5 w-3.5 opacity-70" />
                    <span>{p.name}</span>
                    {desktop ? (
                      <button
                        type="button"
                        className="ml-auto rounded px-1.5 py-0.5 text-[11px] opacity-60 hover:opacity-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          close();
                          desktop?.openWindow(`/p/${p.id}`);
                        }}
                        title="Abrir em nova janela"
                      >
                        <AppWindow className="h-3.5 w-3.5" />
                      </button>
                    ) : (
                      <span className="ml-auto truncate font-mono text-[11px] opacity-60">{p.runtime}</span>
                    )}
                  </Command.Item>
                ))}
                <Command.Item
                  value="novo projeto"
                  onSelect={act(() => runCommand("new-project"))}
                  className={item}
                >
                  <FolderPlus className="h-3.5 w-3.5" /> Novo projeto…
                </Command.Item>
              </Command.Group>
              {mode === "all" ? (
                <>
                  <Command.Group heading="Ir para">
                    {PAGES.filter((p) => FULL_EDITION || !p.full).map((p) => (
                      <Command.Item
                        key={p.command}
                        onSelect={act(() => runCommand(p.command))}
                        className={item}
                      >
                        <p.icon className="h-3.5 w-3.5" /> {p.label}
                      </Command.Item>
                    ))}
                  </Command.Group>
                  <Command.Group heading="Comandos">
                    {FULL_EDITION ? (
                      <Command.Item onSelect={act(() => runCommand("color-themes"))} className={item}>
                        <Palette className="h-3.5 w-3.5" /> Temas (claro e cores)…
                      </Command.Item>
                    ) : null}
                    {FULL_EDITION ? (
                      <Command.Item onSelect={act(() => runCommand("settings"))} className={item}>
                        <Settings className="h-3.5 w-3.5" /> Configurações
                      </Command.Item>
                    ) : null}
                    {isAvailable("new-window") ? (
                      <Command.Item onSelect={act(() => runCommand("new-window"))} className={item}>
                        <AppWindow className="h-3.5 w-3.5" /> Nova janela
                      </Command.Item>
                    ) : null}
                    <Command.Item onSelect={act(() => runCommand("shortcuts"))} className={item}>
                      <Keyboard className="h-3.5 w-3.5" /> Atalhos de teclado
                    </Command.Item>
                  </Command.Group>
                </>
              ) : null}
            </Command.List>
          </Command>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
